/**
 * Concierge drawer channels.
 *
 * Showroom status, and the live-chat form docked into the drawer.
 * The sticky Podium launcher stays hidden. Opening the drawer shows the
 * form itself — there is no start button.
 */
(function () {
  const STATUS_TICK_MS = 60000;
  const CLOSING_SOON_MIN = 60;

  const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const WEEKDAY_NAMES = [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ];

  /* ── Time helpers ───────────────────────────────────────── */

  // Current weekday + minutes-since-midnight in the given IANA timezone.
  function storeNow(timezone) {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date());

    const get = (type) => parts.find((p) => p.type === type)?.value || '';
    const day = WEEKDAYS.indexOf(get('weekday'));
    const hour = parseInt(get('hour'), 10) % 24;
    const minute = parseInt(get('minute'), 10);

    return { day: day < 0 ? new Date().getDay() : day, minutes: hour * 60 + minute };
  }

  function toMinutes(value) {
    const [h, m] = String(value).split(':').map(Number);
    return h * 60 + m;
  }

  function formatTime(value) {
    const [h, m] = String(value).split(':').map(Number);
    const suffix = h >= 12 ? 'PM' : 'AM';
    const hour12 = h % 12 === 0 ? 12 : h % 12;
    return m === 0 ? `${hour12} ${suffix}` : `${hour12}:${String(m).padStart(2, '0')} ${suffix}`;
  }

  // Next day (within a week) that has opening hours.
  function nextOpening(days, fromDay) {
    for (let offset = 1; offset <= 7; offset += 1) {
      const index = (fromDay + offset) % 7;
      if (days[index]) return { index, offset, range: days[index] };
    }
    return null;
  }

  function resolveStatus(schedule) {
    const days = schedule.days || [];
    const { day, minutes } = storeNow(schedule.timezone || 'America/New_York');
    const today = days[day];

    if (today) {
      const open = toMinutes(today[0]);
      const close = toMinutes(today[1]);

      if (minutes >= open && minutes < close) {
        const state = close - minutes <= CLOSING_SOON_MIN ? 'soon' : 'open';
        const label = state === 'soon' ? 'Closing soon' : 'Open now';
        return { day, state, text: `${label} · Closes at ${formatTime(today[1])}` };
      }

      if (minutes < open) {
        return { day, state: 'closed', text: `Closed · Opens today at ${formatTime(today[0])}` };
      }
    }

    const next = nextOpening(days, day);
    if (!next) return { day, state: 'closed', text: 'Closed' };

    const when = next.offset === 1 ? 'tomorrow' : WEEKDAY_NAMES[next.index];
    return { day, state: 'closed', text: `Closed · Opens ${when} at ${formatTime(next.range[0])}` };
  }

  /* ── Status rendering ───────────────────────────────────── */

  // The store profile metaobject may hold { monday: { open, close }, ... }
  // instead of a Sunday-first days array.
  function normalizeSchedule(schedule) {
    if (!schedule || typeof schedule !== 'object') return null;
    if (Array.isArray(schedule.days)) return schedule;
    const names = WEEKDAY_NAMES.map((name) => name.toLowerCase());
    if (!names.some((name) => name in schedule)) return null;
    const days = names.map((name) => {
      const entry = schedule[name];
      return entry && entry.open && entry.close ? [entry.open, entry.close] : null;
    });
    return { timezone: schedule.timezone, days };
  }

  function renderStatus(root) {
    const chip = root.querySelector('[data-concierge-status]');
    const text = root.querySelector('[data-concierge-status-text]');
    if (!chip || !text) return;

    let schedule;
    try {
      schedule = normalizeSchedule(JSON.parse(root.dataset.conciergeHours || '{}'));
    } catch (error) {
      return;
    }
    if (!schedule) return;

    const status = resolveStatus(schedule);

    text.textContent = status.text;
    chip.dataset.state = status.state;
    chip.hidden = false;

    root.querySelectorAll('[data-concierge-day]').forEach((row) => {
      const days = row.dataset.conciergeDay.split(',').map((n) => parseInt(n, 10));
      row.classList.toggle('is-today', days.includes(status.day));
    });
  }

  /* ── Live chat ──────────────────────────────────────────── */

  const PODIUM_WAIT_MS = 8000;
  const PODIUM_DOCK_MS = 9000;
  const PODIUM_REOPEN_MS = 600;
  const PODIUM_POLL_MS = 150;
  let cachedFontFaces = null;

  function keepAmbientAudio() {
    try {
      if (navigator.audioSession) navigator.audioSession.type = 'ambient';
    } catch (error) {}
  }

  function silenceMedia(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('audio, video').forEach((el) => {
      el.muted = true;
      el.defaultMuted = true;
      el.volume = 0;
      el.setAttribute('muted', '');
      el.setAttribute('playsinline', '');
    });
  }

  function collectFontFaces() {
    if (cachedFontFaces !== null) return cachedFontFaces;
    const faces = [];
    for (const sheet of document.styleSheets) {
      let rules;
      try {
        rules = sheet.cssRules;
      } catch (error) {
        continue;
      }
      for (const rule of rules) {
        if (rule.type === CSSRule.FONT_FACE_RULE) faces.push(rule.cssText);
      }
    }
    cachedFontFaces = faces;
    return faces;
  }

  function loadPodium() {
    if (window.SwPodium && typeof window.SwPodium.load === 'function') {
      return window.SwPodium.load().catch(() => null);
    }
    return Promise.resolve(window.PodiumWebChat || null);
  }

  const EMBED_CSS = [
    'html, body, #main, .podium-frame-content, .podium-iframe-container {',
    '  width: 100% !important; height: 100% !important; margin: 0 !important; background: #fff !important;',
    '}',
    '.Modal--widgetLauncherShown, .Modal__Body, .SendSmsPage {',
    '  position: absolute !important; inset: 0 !important; width: 100% !important; height: 100% !important;',
    '  min-width: 0 !important; min-height: 0 !important; max-width: none !important; max-height: none !important; margin: 0 !important;',
    '  border-radius: 0 !important; box-shadow: none !important; transform: none !important; overflow: hidden !important;',
    '}',
    '.SendSmsPage { display: flex !important; flex-direction: column !important; }',
    '.SendSmsPage__HeaderContainer { display: none !important; }',
    '.PodiumPower__Container { flex: 0 0 auto !important; }',
    '.SendSmsPage__MainContent { flex: 1 1 auto !important; min-height: 0 !important; height: auto !important; overflow: hidden !important; }',
    '.SendSmsPage__FormContainer { display: flex !important; flex-direction: column !important; height: 100% !important; min-height: 0 !important; overflow: hidden !important; }',
    '.SendSmsPage__FormContent { flex: 1 1 auto !important; min-height: 0 !important; overflow: auto !important; }',
    '.SendSmsPage__ButtonFader { flex: 0 0 auto !important; height: auto !important; max-height: none !important; overflow: visible !important; }',
    '.Legal, .Legal__text { max-height: none !important; height: auto !important; overflow: visible !important; }',
    '.Legal__text {',
    '  max-width: none !important; width: 84% !important; box-sizing: border-box !important;',
    '  margin-left: auto !important; margin-right: 16px !important;',
    '}',
    '.SendButton {',
    '  display: flex !important; align-items: center !important; justify-content: center !important;',
    '  width: 84% !important; box-sizing: border-box !important;',
    '  margin: 8px 16px 0 auto !important; padding: 12px 40px !important; min-height: 48px !important;',
    '  border: none !important; border-radius: 8px !important;',
    '  color: #fff !important; font-size: 14px !important; font-weight: 500 !important;',
    '  letter-spacing: 0 !important; line-height: 24px !important; text-transform: uppercase !important;',
    '  background: linear-gradient(to top right, #2d94cc 0%, #81bfe0 100%) !important;',
    '  box-shadow: none !important; opacity: 1 !important; cursor: pointer !important;',
    '  transition: transform 160ms ease, filter 160ms ease !important;',
    '}',
    '.SendButton:hover { filter: brightness(1.06) !important; }',
    '.SendButton:active { transform: scale(0.98) !important; }',
    '.SendSmsPage__TextInvitation { display: block !important; flex: 0 0 auto !important; }',
    /* Narrow phones: let the card use the full width instead of Podium's 84% + offset. */
    '@media (max-width: 359px) {',
    '  .SendSmsPage__FormContent, .Legal__text, .SendButton {',
    '    width: calc(100% - 32px) !important; margin-left: 16px !important; margin-right: 16px !important;',
    '  }',
    '}',
    /* Short frames (landscape phones, 320x568): one scroll for the whole form
       instead of pinning legal + Send and squeezing the fields into a sliver. */
    '@media (max-height: 440px) {',
    '  .SendSmsPage { overflow: auto !important; -webkit-overflow-scrolling: touch; overscroll-behavior: contain; }',
    '  .SendSmsPage__MainContent { flex: 0 0 auto !important; overflow: visible !important; }',
    '  .SendSmsPage__FormContainer { height: auto !important; overflow: visible !important; }',
    '  .SendSmsPage__FormContent { flex: 0 0 auto !important; overflow: visible !important; }',
    '}',
    '@media (max-height: 460px) {',
    '  .PodiumPower__Container { display: none !important; }',
    '  .SendSmsPage__ButtonFader { padding-bottom: 12px !important; }',
    '  .SendSmsPage__Center { margin: 0 !important; }',
    '  .SendSmsPage__FormContent { padding: 0 24px !important; }',
    '}',
  ].join('');

  function podiumApi() {
    const api = window.PodiumWebChat;
    return api && typeof api.open === 'function' ? api : null;
  }

  function chatReady() {
    const modal = document.getElementById('podium-modal');
    if (!modal || !modal.contentDocument) return false;
    return !!modal.contentDocument.querySelector('.SendSmsPage, .Modal--widgetLauncherShown');
  }

  function pollUntil(test, timeout) {
    if (test()) return Promise.resolve(true);
    return new Promise((resolve) => {
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (test()) {
          window.clearInterval(timer);
          resolve(true);
        } else if (Date.now() - started > timeout) {
          window.clearInterval(timer);
          resolve(false);
        }
      }, PODIUM_POLL_MS);
    });
  }

  function whenDrawerSettled(drawer) {
    const panel = drawer.querySelector('.ds-concierge');
    if (!panel) return Promise.resolve();
    const transform = getComputedStyle(panel).transform;
    const resting = !transform || transform === 'none' || transform === 'matrix(1, 0, 0, 1, 0, 0)';
    if (resting) return Promise.resolve();
    return new Promise((resolve) => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        resolve();
      };
      panel.addEventListener('transitionend', (event) => {
        if (event.target === panel && event.propertyName === 'transform') finish();
      });
      window.setTimeout(finish, 450);
    });
  }

  function concealModal(modal) {
    if (!modal || modal.dataset.swConcealing === 'true') return;
    if (modal.style.display === 'none' && !modal.classList.contains('is-placed')) return;
    modal.dataset.swConcealing = 'true';
    modal.classList.remove('is-placed');
    modal.style.setProperty('display', 'none', 'important');
    modal.style.setProperty('visibility', 'hidden', 'important');
    modal.style.setProperty('pointer-events', 'none', 'important');
    modal.dataset.swConcealing = 'false';
  }

  function bindChat(root) {
    const fallback = root.querySelector('[data-concierge-chat-fallback]');
    const loading = root.querySelector('[data-concierge-chat-loading]');
    const success = root.querySelector('[data-concierge-chat-success]');
    const panel = root.querySelector('.ds-concierge__panel--chat');
    const frame = root.querySelector('[data-concierge-chat-frame]');
    const drawer = root.closest('concierge-drawer');
    if (!panel || !frame || !drawer || panel.dataset.chatBound === 'true') {
      return { sync() {}, undock() {} };
    }
    panel.dataset.chatBound = 'true';

    let job = 0;
    let docked = false;
    let sent = false;
    let alignRaf = 0;
    let alignObserver = null;
    let bodyObserver = null;

    function chatVisible() {
      return drawer.classList.contains('active') && !panel.hidden;
    }

    function watchModal(modal) {
      if (!modal || modal.dataset.swWatch === 'true') return;
      modal.dataset.swWatch = 'true';
      new MutationObserver(() => {
        if (chatVisible()) return;
        concealModal(modal);
        document.documentElement.classList.remove('podium-chat-docked');
      }).observe(modal, { attributes: true, attributeFilter: ['style', 'class'] });
    }

    function inject(modal) {
      const doc = modal.contentDocument;
      if (!doc || !doc.head) return;
      keepAmbientAudio();
      silenceMedia(doc);
      let node = doc.getElementById('sw-chat-embed');
      if (!node) {
        node = doc.createElement('style');
        node.id = 'sw-chat-embed';
        doc.head.appendChild(node);
      }
      const family = getComputedStyle(document.body).fontFamily;
      const css = EMBED_CSS + '.SendButton{font-family:' + family + ' !important;}';
      if (node.textContent !== css) node.textContent = css;
      if (!doc.getElementById('sw-chat-fonts')) {
        const faces = collectFontFaces();
        if (faces.length) {
          const fonts = doc.createElement('style');
          fonts.id = 'sw-chat-fonts';
          fonts.textContent = faces.join('\n');
          doc.head.appendChild(fonts);
        }
      }
      if (!modal.getAttribute('title')) modal.title = 'Live chat';
    }

    function showSuccess() {
      sent = true;
      stopAlign();
      document.documentElement.classList.add('podium-chat-sent');
      document.documentElement.classList.remove('podium-chat-docked');
      concealModal(document.getElementById('podium-modal'));
      if (loading) loading.hidden = true;
      if (fallback) fallback.hidden = true;
      if (success) success.hidden = false;
      frame.classList.add('is-success');
      const check = success && success.querySelector('.ds-concierge__check');
      if (check) {
        check.classList.remove('is-drawn');
        void check.offsetWidth;
        check.classList.add('is-drawn');
      }
      panel.classList.add('is-embedded');
      root.classList.add('is-chat-embedded');
    }

    function formWasSent(modal, deep = false) {
      const doc = modal && modal.contentDocument;
      if (!doc || !doc.body) return false;
      if (doc.querySelector('.OptInPage, .OptInPage__HeaderContent, .ConfirmationMessage')) return true;
      return deep && /we received your message/i.test(doc.body.innerText || '');
    }

    function watchSubmission(modal) {
      const doc = modal && modal.contentDocument;
      if (!doc || !doc.documentElement || doc.documentElement.dataset.swSubmitWatch === 'true') return;
      doc.documentElement.dataset.swSubmitWatch = 'true';
      const check = () => {
        if (formWasSent(modal, true)) showSuccess();
      };
      new MutationObserver(check).observe(doc.documentElement, {
        childList: true,
        subtree: true,
        characterData: true,
      });
      check();
    }

    function place() {
      const modal = document.getElementById('podium-modal');
      if (sent || (modal && formWasSent(modal))) {
        showSuccess();
        return false;
      }
      if (!modal || !chatVisible()) return false;
      const rect = frame.getBoundingClientRect();
      const viewport = window.visualViewport;
      const offsetTop = viewport ? viewport.offsetTop : 0;
      const offsetLeft = viewport ? viewport.offsetLeft : 0;
      const viewH = viewport ? viewport.height : window.innerHeight;
      const viewW = viewport ? viewport.width : window.innerWidth;
      const top = Math.max(0, rect.top);
      const left = Math.max(0, rect.left);
      const height = Math.min(rect.height, Math.max(0, viewH - top));
      const width = Math.min(rect.width, Math.max(0, viewW - left));
      if (width < 40 || height < 40) return false;

      const box = [
        Math.round(top + offsetTop),
        Math.round(left + offsetLeft),
        Math.round(width),
        Math.round(height),
      ].join(',');
      if (modal.dataset.swBox !== box || !modal.classList.contains('is-placed')) {
        const style = modal.style;
        style.setProperty('display', 'block', 'important');
        style.setProperty('position', 'fixed', 'important');
        style.setProperty('top', `${Math.round(top + offsetTop)}px`, 'important');
        style.setProperty('left', `${Math.round(left + offsetLeft)}px`, 'important');
        style.setProperty('width', `${Math.round(width)}px`, 'important');
        style.setProperty('height', `${Math.round(height)}px`, 'important');
        style.setProperty('right', 'auto', 'important');
        style.setProperty('bottom', 'auto', 'important');
        style.setProperty('margin', '0', 'important');
        style.setProperty('border', '0', 'important');
        style.setProperty('border-radius', '0', 'important');
        style.setProperty('max-width', 'none', 'important');
        style.setProperty('max-height', 'none', 'important');
        style.setProperty('background', '#fff', 'important');
        style.setProperty('z-index', '10000', 'important');
        style.setProperty('visibility', 'visible', 'important');
        style.setProperty('pointer-events', 'auto', 'important');
        modal.classList.add('is-placed');
        modal.dataset.swBox = box;
      }
      inject(modal);
      watchModal(modal);
      watchSubmission(modal);
      return true;
    }

    function present() {
      frame.hidden = false;
      panel.classList.add('is-embedded');
      root.classList.add('is-chat-embedded');
    }

    function schedulePlace() {
      if (alignRaf) return;
      alignRaf = window.requestAnimationFrame(() => {
        alignRaf = 0;
        if (sent) {
          showSuccess();
          return;
        }
        if (!chatVisible()) {
          undock();
          return;
        }
        if (place() && loading) loading.hidden = true;
      });
    }

    function startAlign() {
      if (alignObserver || !('ResizeObserver' in window)) return;
      alignObserver = new ResizeObserver(schedulePlace);
      alignObserver.observe(frame);
    }

    function stopAlign() {
      if (alignObserver) {
        alignObserver.disconnect();
        alignObserver = null;
      }
      if (alignRaf) {
        window.cancelAnimationFrame(alignRaf);
        alignRaf = 0;
      }
    }

    function watchBodyForModal() {
      if (bodyObserver) return;
      bodyObserver = new MutationObserver(() => {
        const modal = document.getElementById('podium-modal');
        if (!modal) return;
        watchModal(modal);
        if (!chatVisible()) concealModal(modal);
        else schedulePlace();
      });
      bodyObserver.observe(document.body, { childList: true });
    }

    function undock() {
      job += 1;
      docked = false;
      stopAlign();
      concealModal(document.getElementById('podium-modal'));
      document.documentElement.classList.remove('podium-chat-docked');
      panel.classList.remove('is-embedded');
      root.classList.remove('is-chat-embedded');
      if (sent) {
        if (loading) loading.hidden = true;
        if (fallback) fallback.hidden = true;
        if (success) success.hidden = false;
      } else {
        frame.classList.remove('is-success');
        if (success) success.hidden = true;
        if (loading) loading.hidden = false;
        if (fallback) fallback.hidden = true;
      }
      if (!drawer.classList.contains('active') && typeof drawer.releaseDocumentScroll === 'function') {
        drawer.releaseDocumentScroll({ restore: false });
      }
    }

    async function dock() {
      const token = ++job;
      present();
      if (fallback) fallback.hidden = true;
      if (loading) loading.hidden = false;
      watchBodyForModal();
      keepAmbientAudio();

      await loadPodium();
      const apiReady = await pollUntil(() => podiumApi() !== null, PODIUM_WAIT_MS);
      if (token !== job || !chatVisible()) return;
      keepAmbientAudio();
      silenceMedia(document);

      // Podium ignores open() until its config requests finish (~2–3s after a
      // cold load), so keep asking until the modal renders.
      let lastOpen = 0;
      const opened = await pollUntil(() => {
        if (chatReady()) return true;
        const api = podiumApi();
        const now = Date.now();
        if (apiReady && api && now - lastOpen >= PODIUM_REOPEN_MS) {
          lastOpen = now;
          api.open();
        }
        return false;
      }, PODIUM_DOCK_MS);
      if (token !== job || !chatVisible()) return;

      if (!opened) {
        concealModal(document.getElementById('podium-modal'));
        if (loading) loading.hidden = true;
        if (fallback) fallback.hidden = false;
        return;
      }

      document.documentElement.classList.add('podium-chat-docked');
      await new Promise((resolve) => {
        window.requestAnimationFrame(() => window.requestAnimationFrame(resolve));
      });
      if (token !== job || !chatVisible()) return;

      if (sent) {
        showSuccess();
        return;
      }

      if (place() && loading) loading.hidden = true;
      docked = true;
      startAlign();
    }

    function sync() {
      if (!chatVisible()) {
        undock();
        return;
      }
      if (sent) {
        showSuccess();
        return;
      }
      present();
      if (docked) {
        place();
        return;
      }
      // Start the Podium download on tap. Wait to dock the iframe until the
      // panel has finished sliding so place() reads a real box.
      loadPodium();
      whenDrawerSettled(drawer).then(() => {
        if (chatVisible() && !docked) dock();
      });
    }

    const realign = () => {
      if (docked) schedulePlace();
    };
    window.addEventListener('resize', realign);
    window.addEventListener('orientationchange', realign);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', realign);
      window.visualViewport.addEventListener('scroll', realign);
    }

    return { sync, undock };
  }

  function bindTabs(root, onSelect) {
    const list = root.querySelector('[data-concierge-tabs]');
    if (!list || list.dataset.tabsBound === 'true') return null;
    list.dataset.tabsBound = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-concierge-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-concierge-panel]'));
    if (tabs.length !== panels.length || !tabs.length) return null;

    const select = (index, { focus = false } = {}) => {
      list.dataset.active = String(index);
      tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) tab.removeAttribute('tabindex');
        else tab.setAttribute('tabindex', '-1');
      });
      panels.forEach((item, i) => {
        item.hidden = i !== index;
      });
      if (focus) tabs[index].focus();
      if (typeof onSelect === 'function') onSelect();
    };

    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(index));
      tab.addEventListener('keydown', (event) => {
        const step = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
        if (!step) return;
        event.preventDefault();
        select((index + step + tabs.length) % tabs.length, { focus: true });
      });
    });

    return () => select(0);
  }

  function init() {
    document.querySelectorAll('[data-concierge-hours]').forEach((root) => {
      renderStatus(root);
      const chat = bindChat(root);
      const resetTabs = bindTabs(root, () => chat.sync());

      if (root.dataset.statusBound === 'true') return;
      root.dataset.statusBound = 'true';

      window.setInterval(() => renderStatus(root), STATUS_TICK_MS);

      const drawer = root.closest('concierge-drawer');
      if (!drawer) return;
      let wasActive = drawer.classList.contains('active');
      new MutationObserver(() => {
        const active = drawer.classList.contains('active');
        if (active === wasActive) return;
        wasActive = active;
        if (!active) {
          chat.undock();
          return;
        }
        renderStatus(root);
        if (resetTabs) resetTabs();
      }).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
